import { useApolloClient } from '@apollo/client';
import { keyBy, uniq } from 'lodash';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { AI_ONBOARDING_MODAL_VARIANT_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import {
  dataToForm,
  formDataToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.utils';
import { useOnboardingPrincipalNodes } from '@/modules/aiOnboarding/AIOnboardingModal/context/useOnboardingPrincipalNodes';
import {
  AiOnboardingModal_HouseholdFragment,
  useAiOnboardingModalBulkCreateMutation,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import {
  getCreatableSuggestions,
  processSuggestionsForReviewCard,
} from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { useFetchReviewCardSuggestions } from '@/modules/aiSuggestionReviewCard/hooks/useFetchReviewCardSuggestions';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { useAcknowledgeAiSuggestionsMutation } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useOnWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnWorkflowTrackerComplete';
// eslint-disable-next-line luminary-rules/no-page-imports
import { Operations as ClientPeopleListViewTableOperations } from '@/pages/ClientDetailsPage/ClientDetailsPeopleOrganizationPage/ListView/graphql/ClientPeopleListViewTable.generated';
import {
  AiSuggestionAcceptanceStatus,
  HouseholdBillableKind,
  WorkflowTrackerKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { Operations as AISuggestionReviewCardV2Operations } from '../graphql/aiSuggestionReviewCardV2.generated';

function useContextValue({
  variant,
  onOpenAIOnboardingModal,
  suggestions,
  additionalReferenceSuggestions,
  onSuggestionCreated,
}: AISuggestionReviewCardProviderProps): AISuggestionReviewCardContext {
  const shouldSeeSuggestions = useCanEditHouseholdData();

  const apolloClient = useApolloClient();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const trackUserEvent = useTrackUserEvent();

  const {
    householdId,
    billableKind,
    primaryClients,
    possibleGrantors,
    entities,
    isTwoClientHousehold,
    documents,
  } = useHouseholdDetailsContext();

  // Track which suggestions are currently being deleted
  const [deletingSuggestionIds, setDeletingSuggestionIds] = useState<string[]>(
    []
  );

  // Track which suggestions are currently being created
  const [creatingSuggestionIds, setCreatingSuggestionIds] = useState<string[]>(
    []
  );

  // Track whether a bulk "create all" operation is in progress
  const [isCreatingAll, setIsCreatingAll] = useState<boolean>(false);

  // Keep successfully acknowledged suggestions hidden until the source data catches up.
  const [suppressedSuggestionIds, setSuppressedSuggestionIds] = useState<
    string[]
  >([]);

  // Determine if we should use external suggestions or fetch internally
  const isUsingExternalSuggestions = Array.isArray(suggestions);

  // Fetch suggestions for the review card only when not using external suggestions.
  const { data: fetchedSuggestionsData, refetch: refetchSuggestions } =
    useFetchReviewCardSuggestions({
      householdId: householdId ?? undefined,
      skip: isUsingExternalSuggestions || !shouldSeeSuggestions,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        reportError(
          `[AISuggestionReviewCardProvider] error fetching suggestions: householdID=${householdId}, variant=${variant}`,
          error
        );
      },
    });

  const reviewCardSuggestions = useMemo(() => {
    if (isUsingExternalSuggestions) {
      return suggestions ?? [];
    }

    const fetchedSuggestions = getNodes(
      fetchedSuggestionsData?.aiSuggestions
    ) as AiSuggestionForReviewCard[];

    return fetchedSuggestions;
  }, [fetchedSuggestionsData, isUsingExternalSuggestions, suggestions]);

  const lookupSuggestions = useMemo(() => {
    if (!isUsingExternalSuggestions) {
      return reviewCardSuggestions;
    }

    return Object.values(
      keyBy(
        [...reviewCardSuggestions, ...(additionalReferenceSuggestions ?? [])],
        'id'
      )
    );
  }, [
    additionalReferenceSuggestions,
    isUsingExternalSuggestions,
    reviewCardSuggestions,
  ]);

  const { reviewSuggestions, otherSuggestions } = useMemo(() => {
    return processSuggestionsForReviewCard(reviewCardSuggestions, variant);
  }, [reviewCardSuggestions, variant]);

  const suggestionsByID = useMemo(
    () => keyBy(lookupSuggestions, 'id'),
    [lookupSuggestions]
  );

  const { options: assetCategoryOptions } = useAssetCategoryOptions();

  // Resolve principals (grantors/owners/donors) directly from their
  // `principalNodeIDs` by fetching the referenced KG V2 nodes once for all rows.
  const { principalNodesById, loading: principalNodesLoading } =
    useOnboardingPrincipalNodes({
      householdId: householdId ?? '',
      suggestions: lookupSuggestions,
    });

  // People suggestions can be accepted without user action once a workflow
  // completes, so the people list itself (not just the pending suggestions)
  // needs to be refetched to reflect the new profiles.
  const refetchPeopleList = useCallback(() => {
    void apolloClient.refetchQueries({
      include: [
        ClientPeopleListViewTableOperations.Query.ClientPeopleListViewTable,
      ],
    });
  }, [apolloClient]);

  // Watch for document extraction workflow completion to refetch suggestions
  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentExtraction,
    onCompleted: () => {
      diagnostics.debug(
        '[AISuggestionReviewCardProvider] Document extraction completed, refetching suggestions',
        { householdId }
      );
      void refetchSuggestions();
      refetchPeopleList();
    },
    enabled:
      !isUsingExternalSuggestions && !!householdId && shouldSeeSuggestions,
  });

  // Watch for household suggestion regeneration completion to refetch suggestions
  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2HouseholdRegen,
    onCompleted: () => {
      diagnostics.debug(
        '[AISuggestionReviewCardProvider] Household regen completed, refetching suggestions',
        { householdId }
      );
      void refetchSuggestions();
      refetchPeopleList();
    },
    enabled:
      !isUsingExternalSuggestions && !!householdId && shouldSeeSuggestions,
  });

  // Set up the acknowledgement mutation.
  const [acknowledgeSuggestion] = useAcknowledgeAiSuggestionsMutation({
    refetchQueries: [
      AISuggestionReviewCardV2Operations.Query
        .FetchAISuggestionsForReviewCardV2,
    ],
  });

  // Set up bulk create mutation for creating things from suggestions.
  const [bulkCreateMutation] = useAiOnboardingModalBulkCreateMutation({
    refetchQueries: [
      AISuggestionReviewCardV2Operations.Query
        .FetchAISuggestionsForReviewCardV2,
      ClientPeopleListViewTableOperations.Query.ClientPeopleListViewTable,
    ],
  });

  // Memoized suggestions to show in the review card (excluding any in-flight)
  const filteredReviewSuggestions = useMemo(() => {
    return reviewSuggestions.filter(
      (suggestion) =>
        !suppressedSuggestionIds.includes(suggestion.id) &&
        !deletingSuggestionIds.includes(suggestion.id) &&
        !creatingSuggestionIds.includes(suggestion.id)
    );
  }, [
    reviewSuggestions,
    suppressedSuggestionIds,
    deletingSuggestionIds,
    creatingSuggestionIds,
  ]);

  const creatableSuggestions = useMemo(
    () =>
      getCreatableSuggestions(filteredReviewSuggestions, {
        possibleGrantors: possibleGrantors || [],
        entities: entities || [],
        assetCategoryOptions,
        isTwoClientHousehold,
        principalNodesById,
      }),
    [
      filteredReviewSuggestions,
      possibleGrantors,
      entities,
      assetCategoryOptions,
      isTwoClientHousehold,
      principalNodesById,
    ]
  );

  // Helper function for creating entities from suggestions
  const createEntitiesFromSuggestions = useCallback(
    async (
      suggestionList: AiSuggestionForReviewCard[],
      isBulkCreate = false
    ) => {
      if (!householdId) {
        // This should not happen; just for type safety
        reportError(
          '[AISuggestionReviewCardProvider][processSuggestions] no household ID'
        );
        return;
      }

      // Add the suggestion IDs to the creating list
      const suggestionIds = suggestionList.map((suggestion) => suggestion.id);
      setCreatingSuggestionIds((prev) => [...prev, ...suggestionIds]);

      // Set the bulk creation flag if this is a "create all" operation
      if (isBulkCreate) {
        setIsCreatingAll(true);
      }

      // Build household object from current context.
      // Include documents from context so that entity-document relationship
      // filtering works correctly (to exclude soft-deleted documents).
      const household: AiOnboardingModal_HouseholdFragment = {
        id: householdId,
        billableKind: billableKind || HouseholdBillableKind.Billable,
        possiblePrimaryClients: primaryClients ?? [],
        possibleGrantors: possibleGrantors ?? [],
        isTwoGrantor: isTwoClientHousehold,
        documents: documents,
        entities: { edges: entities?.map((e) => ({ node: e })) || [] },
      };

      let formInput: ReturnType<typeof formDataToInput>;
      try {
        // Transform suggestions into form data
        const formData = dataToForm(suggestionList, household, {
          principalNodesById,
        });

        // Transform form data into the mutation create inputs and suggestions to acknowledge
        formInput = formDataToInput(
          formData,
          AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant].formPaths,
          household,
          suggestionsByID
        );
      } catch (error) {
        setCreatingSuggestionIds((prev) =>
          prev.filter((id) => !suggestionIds.includes(id))
        );
        if (isBulkCreate) {
          setIsCreatingAll(false);
        }
        reportError(
          'Failed to prepare suggestions for creation',
          error instanceof Error ? error : new Error(String(error)),
          {
            householdId,
            variant,
            suggestionIds: suggestionIds.join(','),
          }
        );
        showFeedback('Failed to create from suggestions. Please try again.', {
          variant: 'error',
        });
        return;
      }

      const { acceptedSuggestionIDs, ...rest } = formInput;

      // Execute the bulk create mutation first
      await bulkCreateMutation({
        variables: {
          ...rest,
          acknowledgeSuggestionsInput: [
            ...acceptedSuggestionIDs.map((id) => ({
              suggestionID: id,
              status: AiSuggestionAcceptanceStatus.Accepted,
            })),
          ],
        },
        onCompleted: async () => {
          // Stop showing the just-acknowledged suggestions even if upstream data lags.
          setCreatingSuggestionIds((prev) =>
            prev.filter((id) => !suggestionIds.includes(id))
          );
          setSuppressedSuggestionIds((prev) =>
            uniq([...prev, ...acceptedSuggestionIDs])
          );
          // Clear the bulk creation flag if this was a bulk operation
          if (isBulkCreate) {
            setIsCreatingAll(false);
          }
          showFeedback('Successfully created', { variant: 'success' });
          await apolloClient.refetchQueries({
            updateCache(cache) {
              const householdCacheId = cache.identify({
                __typename: 'Household',
                id: householdId,
              });

              cache.evict({ fieldName: 'aiSuggestions' });
              cache.evict({ fieldName: 'entities' });
              cache.evict({ fieldName: 'clientProfiles' });
              cache.evict({ fieldName: 'clientOrganizations' });
              // The created records can resolve KG V2 principal nodes (e.g. a
              // created trust becomes another suggestion's eligible owner), so
              // the cached node resolution edges must be refetched too —
              // otherwise dependent rows keep their stale "not yet created"
              // warning until a hard reload.
              cache.evict({ fieldName: 'fetchHouseholdKGV2Nodes' });
              if (householdCacheId) {
                cache.evict({
                  id: householdCacheId,
                  fieldName: 'possibleGrantors',
                });
              }
              cache.gc();
            },
          });
          // Notify parent component that a suggestion was created
          // (useful for refetching externally-provided suggestions)
          onSuggestionCreated?.();
        },
        onError: (error) => {
          // Clear only the suggestions that failed in this specific operation
          setCreatingSuggestionIds((prev) =>
            prev.filter((id) => !suggestionIds.includes(id))
          );
          // Clear the bulk creation flag if this was a bulk operation
          if (isBulkCreate) {
            setIsCreatingAll(false);
          }
          reportError('Failed to create from suggestions', error);
          showFeedback('Failed to create from suggestions. Please try again.', {
            variant: 'error',
          });
        },
      });
    },
    [
      householdId,
      billableKind,
      primaryClients,
      possibleGrantors,
      isTwoClientHousehold,
      documents,
      entities,
      suggestionsByID,
      principalNodesById,
      variant,
      bulkCreateMutation,
      reportError,
      showFeedback,
      apolloClient,
      onSuggestionCreated,
    ]
  );

  const onCreateClick = useCallback(
    (suggestionID: string) => {
      const suggestion = suggestionsByID[suggestionID];
      if (!suggestion) {
        reportError(
          `[AISuggestionReviewCardProvider][onCreateClick] could not find suggestion with ID: ${suggestionID}`
        );
        return;
      }
      trackUserEvent('ai_suggestion accept suggestion', {
        id: suggestionID,
        kind: suggestion.kind,
      });
      void createEntitiesFromSuggestions([suggestion]);
    },
    [
      suggestionsByID,
      trackUserEvent,
      createEntitiesFromSuggestions,
      reportError,
    ]
  );

  const onCreateAllClick = useCallback(() => {
    if (creatableSuggestions.length === 0) {
      return;
    }

    trackUserEvent('ai_suggestion accept suggestions', {
      kinds: uniq(creatableSuggestions.map((r) => r.kind)).join(', '),
      count: creatableSuggestions.length,
    });
    void createEntitiesFromSuggestions(creatableSuggestions, true);
  }, [trackUserEvent, createEntitiesFromSuggestions, creatableSuggestions]);

  const onReviewClick = useCallback(
    (suggestionID: string) => {
      const suggestion = suggestionsByID[suggestionID];
      if (!suggestion) {
        reportError(
          `[AISuggestionReviewCardProvider][onReviewClick] could not find suggestion with ID: ${suggestionID}`
        );
        return;
      }
      trackUserEvent('ai_suggestion review suggestion', {
        id: suggestionID,
        kind: suggestion.kind,
      });
      onOpenAIOnboardingModal({
        initialSelectedSuggestion: suggestion,
      });
    },
    [suggestionsByID, trackUserEvent, onOpenAIOnboardingModal, reportError]
  );

  const onReviewAllClick = useCallback(() => {
    trackUserEvent('ai_suggestion review all');

    onOpenAIOnboardingModal({
      initialSelectedSuggestion: undefined,
    });
  }, [onOpenAIOnboardingModal, trackUserEvent]);

  const onDeleteClick = useCallback(
    (suggestionID: string) => {
      const suggestion = suggestionsByID[suggestionID];
      if (!suggestion) {
        reportError(
          `[AISuggestionReviewCardProvider][onDeleteClick] could not find suggestion with ID: ${suggestionID}`
        );
        return;
      }

      trackUserEvent('ai_suggestion reject suggestion', {
        id: suggestionID,
        kind: suggestion.kind,
      });

      // Add the suggestion ID to the deleting list
      setDeletingSuggestionIds((prev) => [...prev, suggestionID]);

      void acknowledgeSuggestion({
        variables: {
          input: [
            {
              suggestionID,
              status: AiSuggestionAcceptanceStatus.Rejected,
            },
          ],
        },
        onCompleted: async () => {
          // Stop showing the rejected suggestion even if upstream data lags.
          setDeletingSuggestionIds((prev) =>
            prev.filter((id) => id !== suggestionID)
          );
          setSuppressedSuggestionIds((prev) => uniq([...prev, suggestionID]));
          showFeedback('Suggestion deleted successfully', {
            variant: 'success',
          });
          await apolloClient.refetchQueries({
            updateCache(cache) {
              cache.evict({ fieldName: 'aiSuggestions' });
              cache.gc();
            },
          });
        },
        onError: (error) => {
          // Clear only the suggestion that failed in this specific operation
          setDeletingSuggestionIds((prev) =>
            prev.filter((id) => id !== suggestionID)
          );
          reportError('Failed to delete suggestion', error);
          showFeedback('Failed to delete suggestion. Please try again.', {
            variant: 'error',
          });
        },
      });
    },
    [
      acknowledgeSuggestion,
      reportError,
      suggestionsByID,
      trackUserEvent,
      apolloClient,
      showFeedback,
    ]
  );

  const onSuggestionMarkedDuplicate = useCallback(
    (suggestionID: string) => {
      setSuppressedSuggestionIds((prev) => uniq([...prev, suggestionID]));
      onSuggestionCreated?.();
    },
    [onSuggestionCreated]
  );

  return {
    suggestionsByID,
    principalNodesById,
    principalNodesLoading,
    reviewSuggestions: filteredReviewSuggestions,
    creatableSuggestions,
    otherSuggestions,
    onCreateClick,
    onCreateAllClick,
    onReviewAllClick,
    onReviewClick,
    onDeleteClick,
    onSuggestionMarkedDuplicate,
    deletingSuggestionIds,
    creatingSuggestionIds,
    isCreatingAll,
  };
}

interface AISuggestionReviewCardProviderProps {
  /**
   * The AI Onboarding modal variant this review card corresponds to.
   */
  variant: AIOnboardingModalVariant;
  /**
   * Callback to open the AI onboarding modal to locate or edit a suggestion.
   */
  onOpenAIOnboardingModal: ({
    initialSelectedSuggestion,
  }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => void;
  /**
   * Optional externally-provided suggestions. When provided, the provider will
   * derive its state from these and skip internal polling/fetching.
   */
  suggestions?: AiSuggestionForReviewCard[];
  /**
   * Suggestions needed only for resolving references from visible suggestions.
   */
  additionalReferenceSuggestions?: AiSuggestionForReviewCard[];
  /**
   * Optional callback invoked after a suggestion is successfully created.
   * Useful for refetching externally-provided suggestions.
   */
  onSuggestionCreated?: () => void;
}

export function AISuggestionReviewCardProvider({
  children,
  ...props
}: PropsWithChildren<AISuggestionReviewCardProviderProps>) {
  const value = useContextValue(props);

  return (
    <AISuggestionReviewCardContext.Provider value={value}>
      {children}
    </AISuggestionReviewCardContext.Provider>
  );
}
